import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-03-14 15:28:35 +0100 (Sat, 14 Mar 2026) $
// Author      : Olli Powroznik
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 161542 $
// Purpose     : extended custom styled XMenu - Toolbar with dynamic space management
// =============================================================================
// ---Third-party imports
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
// ---Framework imports
import { useDimensions, useSelector, useUserSetting } from 'uix/hooks';
import log from 'uix/logging';
import { VIcon } from 'uix/graphics';
import { Sel } from 'uix/registries';
// ---Local imports
import { XMenu, ChildPopupRegistry } from './menu';
import { DebugFeature } from './debug';
import styles from './toolbar.css';
const Toolbar = ({ responsive = false, attached = false, titled = true, ribbons = false, vertical = false, children, floated, disabled, //style
colored = false, size = 'default', className, ...props })=>{
    let [headerStyle] = useUserSetting('headerStyle');
    if (headerStyle == 'compact') {
        ribbons = false;
    }
    let childRefs = useRef([]);
    let scrollRef = useRef([]);
    let [toolbarDim, dimRef] = useDimensions({
        delta: 15
    });
    let [groupStates, setGroupStates] = useState([]);
    let [overflow, setOverflow] = useState(false);
    let [hiddenItemsState, setHiddenItemsState] = useState({});
    let resolveAttach = ()=>{
        if (attached === true && !vertical) return 'top';
        if (vertical && attached === true) return 'left';
        return attached;
    };
    let attachClass = {
        top: styles.topAttach,
        bottom: styles.bottomAttach,
        left: styles.leftAttach,
        right: styles.rightAttach,
        notAttach: styles.notAttach
    }[resolveAttach()] || styles.notAttach;
    let sizeClass = {
        mini: styles.mini,
        tiny: styles.tiny,
        small: styles.small,
        default: styles.default,
        large: styles.large,
        huge: styles.huge
    }[size];
    // Helper to update hiddenItemsState
    let updateHiddenItems = useCallback((groupId, newHiddenCount, isCollapsed = false)=>{
        setHiddenItemsState((prevHidden)=>{
            let currentState = prevHidden[groupId] || {};
            // Update nur bei Änderungen
            if (currentState.hiddenCount !== newHiddenCount || currentState.isCollapsed !== isCollapsed) {
                return {
                    ...prevHidden,
                    [groupId]: {
                        hiddenCount: newHiddenCount,
                        isCollapsed
                    }
                };
            }
            return prevHidden;
        });
    }, []);
    // check if all groups are collapsed still overflowing container
    let checkOverflow = (availableSpace)=>{
        let allGroupsProcessed = groupStates.every((group)=>{
            let { hiddenCount, totalItems, isCollapsed } = groupDetails[group.id];
            // check if all groups are max. possible collapsed
            return hiddenCount >= totalItems - 2 || isCollapsed;
        });
        return allGroupsProcessed && availableSpace < 0;
    };
    // Helper for group details
    const calculateGroupDetails = (groupStates, hiddenItemsState)=>{
        return groupStates.map((group, index)=>{
            let isCollapsed = hiddenItemsState[index]?.isCollapsed;
            let hiddenCount = hiddenItemsState[group.id]?.hiddenCount || 0;
            let totalItems = group.childDimensions.length;
            return {
                group,
                hiddenCount,
                totalItems,
                isCollapsed
            };
        });
    };
    let { availableSpace, groupDetails } = useMemo(()=>{
        const groupsWidth = groupStates.reduce((acc, group)=>acc + (group?.itemWidth || 0), 0);
        const availableSpace = responsive && groupStates.length > 0 ? Math.round(toolbarDim.width - groupsWidth) || 0 : 0;
        const groupDetails = calculateGroupDetails(groupStates, hiddenItemsState);
        return {
            availableSpace,
            groupDetails
        };
    }, [
        toolbarDim.width,
        groupStates,
        hiddenItemsState
    ]);
    // Decrease visible items when there is not enough space
    let hideItems = (availableSpace)=>{
        let groupIndex = groupStates.length - 1 // Start at the last group
        ;
        let shouldCollapse = true;
        let minVisibleItems = 2;
        // hide toolbar items
        while(groupIndex >= 0 && availableSpace < 0){
            let { group, hiddenCount, totalItems } = groupDetails[groupIndex];
            let allowedWidth = group.fontSize * 3;
            // let groups collapse
            if (ribbons && totalItems <= minVisibleItems) {
                if (totalItems === 1 && hiddenCount === 0) {
                    if (group.childDimensions[0] > allowedWidth) {
                        updateHiddenItems(group.id, totalItems, true);
                    }
                } else {
                    if (group.childDimensions[0] + group.childDimensions[1] > allowedWidth) {
                        updateHiddenItems(group.id, totalItems, true);
                    }
                }
            }
            if (hiddenCount < totalItems - minVisibleItems) {
                // Hide 2 items initially to offset the "More" button's space.
                let newHiddenCount = hiddenCount === 0 ? 2 : hiddenCount + 1;
                if (totalItems - newHiddenCount === 1) {
                    updateHiddenItems(group.id, totalItems, true);
                    break;
                }
                updateHiddenItems(group.id, newHiddenCount);
                shouldCollapse = false;
                break;
            }
            groupIndex--;
        }
        if (shouldCollapse && ribbons) {
            groupIndex = groupStates.length - 1; // Reset groupIndex to start from the last group again
            while(groupIndex >= 0 && availableSpace < 0){
                let { group, hiddenCount, totalItems } = groupDetails[groupIndex];
                if (totalItems <= minVisibleItems) {
                    groupIndex--;
                    continue;
                }
                // collapse group if all items hidden
                if (hiddenCount === totalItems - minVisibleItems || totalItems == 3) {
                    updateHiddenItems(group.id, totalItems, true); // Set collapse to true
                }
                groupIndex--;
            }
        }
    };
    let showItems = (availableSpace)=>{
        let groupIndex = 0;
        while(groupIndex < groupStates.length && availableSpace > 0){
            let { group, hiddenCount, totalItems, isCollapsed } = groupDetails[groupIndex];
            if (hiddenCount > 0 || isCollapsed) {
                // Get width of the next hidden item
                let nextItemWidth = group.childDimensions[totalItems - hiddenCount];
                // Check if only two items are hidden
                if (hiddenCount >= 2) {
                    // Get the width of the next two hidden items
                    let secondNextItemWidth = hiddenCount > 1 ? group.childDimensions[totalItems - hiddenCount + 1] : 0;
                    // Check if both items fit in the available space
                    if (availableSpace - (nextItemWidth + secondNextItemWidth) >= 20) {
                        // Enough space to display both items
                        updateHiddenItems(group.id, hiddenCount - 2);
                        availableSpace -= nextItemWidth + secondNextItemWidth;
                    } else {
                        // Not enough space for both -> check next group
                        groupIndex++;
                    }
                } else {
                    // Standard logic: Check if there is enough space for the next item
                    if (availableSpace - nextItemWidth >= 20) {
                        let newHiddenItems = hiddenCount - 1;
                        updateHiddenItems(group.id, newHiddenItems);
                        // Deduct the width of the item from available space
                        availableSpace -= nextItemWidth;
                    } else {
                        groupIndex++;
                    }
                }
            } else {
                // No hidden items in this group, go to the next group
                groupIndex++;
            }
        }
    };
    let setResponsiveGroup = (id, itemWidth, childDimensions, fontSize)=>{
        setGroupStates((prev)=>{
            let updatedStates = [
                ...prev
            ];
            updatedStates[id] = {
                id,
                itemWidth,
                childDimensions: childDimensions || [],
                fontSize: fontSize || 0
            };
            return updatedStates;
        });
    };
    useEffect(()=>{
        if (responsive && groupStates.length > 0) {
            setOverflow(checkOverflow(availableSpace));
            if (availableSpace < 0) {
                hideItems(availableSpace);
            } else if (availableSpace > 0) {
                showItems(availableSpace);
            }
        }
    }, [
        availableSpace
    ]);
    // Helper function to process children
    let responsiveChildren = (children, { hiddenItemsState, setResponsiveGroup, childRefs, styles })=>{
        let validIdx = 0 // Counter for ToolbarGroups
        ;
        return Array.isArray(children) && children.map((child, i)=>{
            // Handle DebugFeature wrapper
            if (child?.type?.displayName === 'DebugFeature') {
                let firstChild = Array.isArray(child.props.children) ? child.props.children[0] : child.props.children;
                if (firstChild?.type?.displayName === 'ToolbarGroup') {
                    let id = validIdx;
                    let hiddenItems = hiddenItemsState[id]?.hiddenCount || 0;
                    let isCollapsed = hiddenItemsState[id]?.isCollapsed || false;
                    validIdx++;
                    return /*#__PURE__*/ _jsx(DebugFeature, {
                        children: /*#__PURE__*/ React.cloneElement(firstChild, {
                            id: id,
                            setResponsiveGroup,
                            hiddenItemsCount: hiddenItems,
                            isCollapsed,
                            style: firstChild.props.style,
                            disabled: disabled
                        })
                    }, `debug-${i}`);
                }
            }
            if (child?.type?.displayName === 'ToolbarGroup') {
                let id = validIdx;
                let hiddenItems = hiddenItemsState[id]?.hiddenCount || 0;
                let isCollapsed = hiddenItemsState[id]?.isCollapsed || false;
                validIdx++;
                return /*#__PURE__*/ _jsx("div", {
                    title: child.props.title,
                    style: child.props.style,
                    className: clsx(child?.props?.position === 'right' && styles.right, styles.responsive),
                    ref: (el)=>childRefs.current[id] = el,
                    children: /*#__PURE__*/ React.cloneElement(child, {
                        id: id,
                        setResponsiveGroup,
                        hiddenItemsCount: hiddenItems,
                        isCollapsed,
                        disabled: disabled,
                        style: child.props.style
                    })
                }, i);
            }
            return null;
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        style: {
            width: responsive ? '100%' : 'inherit'
        },
        ref: dimRef,
        children: /*#__PURE__*/ _jsx(XMenu, {
            vertical: vertical,
            attached: attached ? true : false,
            floated: floated,
            size: size === 'default' ? null : size,
            className: clsx(className, styles.toolbar, attachClass, sizeClass, overflow && styles.overflowing, ribbons && styles.ribbons, !titled && styles.hideTitle, vertical && styles.vertical, floated === 'right' && styles.floated, colored && styles.colored),
            ...props,
            children: responsive ? /*#__PURE__*/ _jsxs("div", {
                className: styles.scrollContainer,
                ref: scrollRef,
                children: [
                    overflow && /*#__PURE__*/ _jsx(OverflowScrollButtons, {
                        scrollRef: scrollRef,
                        toolbarWidth: toolbarDim.width
                    }),
                    responsiveChildren(children, {
                        hiddenItemsState,
                        setResponsiveGroup,
                        childRefs,
                        disabled: disabled,
                        styles
                    })
                ]
            }) : children
        })
    });
};
const OverflowScrollButtons = ({ scrollRef, toolbarWidth })=>{
    let [isScrollableLeft, setIsScrollableLeft] = useState(false);
    let [isScrollableRight, setIsScrollableRight] = useState(false);
    let offset = toolbarWidth - 45 || 200;
    useEffect(()=>{
        if (scrollRef.current) {
            handleScroll();
            scrollRef.current.addEventListener('scroll', handleScroll);
        }
        return ()=>{
            if (scrollRef.current) {
                scrollRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [
        scrollRef,
        toolbarWidth
    ]);
    let handleScroll = ()=>{
        if (scrollRef.current) {
            let currentScrollLeft = scrollRef.current.scrollLeft;
            let maxScroll = scrollRef.current.scrollWidth - toolbarWidth;
            setIsScrollableLeft(currentScrollLeft > 0);
            setIsScrollableRight(currentScrollLeft < maxScroll);
        }
    };
    let scroll = (direction)=>{
        scrollRef.current.scrollLeft += direction === 'right' ? offset : -offset;
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                onClick: isScrollableLeft ? ()=>scroll('left') : null,
                className: clsx(styles.scrollButton, styles.left, !isScrollableLeft && styles.disabled),
                children: /*#__PURE__*/ _jsx(VIcon, {
                    disabled: !isScrollableLeft,
                    name: "chevron left"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                onClick: isScrollableRight ? ()=>scroll('right') : null,
                className: clsx(styles.scrollButton, styles.right, !isScrollableRight && styles.disabled),
                children: /*#__PURE__*/ _jsx(VIcon, {
                    disabled: !isScrollableRight,
                    name: "chevron right"
                })
            })
        ]
    });
};
// needed by ghost-toolbar items preventing cloned interaction
const stopInteraction = (children)=>{
    return React.Children.map(children, (child, i)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) return child;
        let displayName = child.type?.displayName || child.type?.name;
        if (displayName) {
            return /*#__PURE__*/ React.cloneElement(child, {
                key: `ghost-${i}`,
                disabled: true,
                open: false,
                on: []
            });
        }
        let newProps = {};
        if (child.props?.children) {
            newProps.children = stopInteraction(child.props.children);
        }
        return /*#__PURE__*/ React.cloneElement(child, newProps);
    });
};
const flattenChildren = (children, exclude = [])=>{
    if (!children) return [];
    children = Array.isArray(children) ? children : [
        children
    ];
    return children.reduce((flattened, child)=>{
        if (!child) return flattened;
        let displayName = child.type?.displayName;
        // Keep DebugFeature as Wrapper
        if (displayName === 'DebugFeature') {
            return flattened.concat(/*#__PURE__*/ _jsx(DebugFeature, {
                children: flattenChildren(child.props.children, exclude)
            }, child.key || `debug-${Math.random()}`));
        }
        // stop flatten on excluded displayNames
        let excluding = exclude.includes(displayName);
        if (excluding) {
            return flattened.concat(child);
        }
        // if no child → continue recursive flatten
        if (child?.props?.children) {
            return flattened.concat(flattenChildren(child.props.children, exclude));
        }
        return flattened.concat(child);
    }, []);
};
const Group = ({ position, separator = true, children, title, icon, style, show = true, //responsive Toolbar:
id, setResponsiveGroup, hiddenItemsCount, isCollapsed, disabled })=>{
    let dimRef = useRef();
    let childRefs = useRef([]);
    let flatChildren = flattenChildren(children, [
        'ToolbarMore',
        'XPopupItem'
    ]);
    let [fontSize, setFontSize] = useState([]);
    let [groupDimension, setGroupDimensions] = useState([]);
    let childDimensions = childRefs.current.map((ref)=>ref?.getBoundingClientRect()?.width || 0);
    useEffect(()=>{
        if (setResponsiveGroup) {
            let styles = window?.getComputedStyle(dimRef?.current);
            styles ? setFontSize(parseFloat(styles?.fontSize)) : '16';
        }
    }, []);
    useEffect(()=>{
        if (setResponsiveGroup) {
            let childDimensions = childRefs.current.map((ref)=>ref?.getBoundingClientRect()?.width || 0);
            setResponsiveGroup(id, groupDimension, childDimensions, fontSize);
        }
    }, [
        groupDimension
    ]);
    useEffect(()=>{
        if (setResponsiveGroup) {
            setGroupDimensions(Math.round(dimRef.current?.getBoundingClientRect().width) || 0);
        }
    }, [
        flatChildren,
        hiddenItemsCount
    ]);
    let visibleItems = hiddenItemsCount > 0 ? flatChildren.slice(0, flatChildren.length - hiddenItemsCount) : flatChildren;
    let hiddenItems = hiddenItemsCount > 0 ? flatChildren.slice(-hiddenItemsCount) : [];
    let countActiveItems = (items)=>{
        let count = 0;
        let checkProps = (props)=>{
            if (props.active === true || props.value === true) {
                count++;
            }
            if (props.children) {
                if (Array.isArray(props.children)) {
                    props.children.forEach((child)=>{
                        if (child.props) checkProps(child.props);
                    });
                } else if (props.children.props) {
                    checkProps(props.children.props);
                }
            }
        };
        items.forEach((item)=>{
            if (item) checkProps(item);
        });
        return count;
    };
    let activeItems = countActiveItems(hiddenItems.map((child)=>child?.props));
    let activeCollapsedItems = countActiveItems(flatChildren.map((child)=>child?.props));
    let responsiveContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            visibleItems.map((child, i)=>/*#__PURE__*/ React.cloneElement(child, {
                    key: i,
                    disabled
                })),
            hiddenItems.length > 0 && /*#__PURE__*/ _jsx(More, {
                disabled: disabled,
                activeItems: activeItems,
                children: flattenChildren(hiddenItems)
            })
        ]
    });
    if (!show) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: !setResponsiveGroup ? /*#__PURE__*/ _jsxs("div", {
            style: style,
            className: clsx(styles.group, position === 'right' && styles.right),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.menu,
                    children: [
                        children,
                        separator && /*#__PURE__*/ _jsx(XMenu.Separator, {
                            className: clsx(!separator && styles.hidden, styles.separator)
                        })
                    ]
                }),
                title && /*#__PURE__*/ _jsx("span", {
                    className: styles.title,
                    children: title
                })
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    ref: dimRef,
                    style: style,
                    className: clsx(styles.group, position === 'right' && styles.right),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.menu,
                            children: isCollapsed ? /*#__PURE__*/ _jsx(More, {
                                disabled: disabled || flattenChildren(hiddenItems).every((child)=>child?.props?.disabled),
                                prominent: true,
                                activeItems: activeCollapsedItems,
                                icon: icon,
                                children: flattenChildren(hiddenItems, 'XPopupItem')
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    responsiveContent,
                                    /*#__PURE__*/ _jsx(XMenu.Separator, {
                                        className: clsx(!separator && styles.hidden, styles.separator)
                                    })
                                ]
                            })
                        }),
                        title && /*#__PURE__*/ _jsx("span", {
                            className: styles.title,
                            children: title
                        })
                    ]
                }, id),
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx(styles.group, styles.ghost),
                    style: {
                        pointerEvents: 'none',
                        userSelect: 'none'
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.menu,
                            children: stopInteraction(flatChildren).map((child, i)=>/*#__PURE__*/ _jsxs("div", {
                                    ref: (el)=>childRefs.current[i] = el,
                                    children: [
                                        childDimensions.length > 0 && /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                position: 'absolute',
                                                fontSize: '0.8em',
                                                bottom: '-5px'
                                            },
                                            children: Math.round(childDimensions[i])
                                        }),
                                        /*#__PURE__*/ React.cloneElement(child)
                                    ]
                                }, i))
                        }),
                        groupDimension && /*#__PURE__*/ _jsx("span", {
                            children: groupDimension
                        })
                    ]
                })
            ]
        })
    });
};
const More = ({ children, icon, caption, activeItems, prominent, title, vertical = false, position = 'bottom left', closeOnChildClick = false, onClick: onClickProp, ...props })=>{
    let i18n = useSelector(Sel.i18n);
    // Track how many descendant XPopupItems are currently open (sticky children).
    // When any are open, pass keepOpen so More does not close on outside click.
    const [childOpenCount, setChildOpenCount] = useState(0);
    const closeCallbacks = useRef(new Set());
    const isMoreOpenRef = useRef(false);
    const registry = useMemo(()=>({
            register: (closeFn)=>{
                setChildOpenCount((c)=>c + 1);
                if (closeFn) closeCallbacks.current.add(closeFn);
            },
            unregister: (closeFn)=>{
                setChildOpenCount((c)=>Math.max(0, c - 1));
                if (closeFn) closeCallbacks.current.delete(closeFn);
            },
            closeAll: ()=>{
                closeCallbacks.current.forEach((fn)=>fn());
                closeCallbacks.current.clear();
                setChildOpenCount(0);
            }
        }), []);
    // onClick fires synchronously inside onClickOpen (after setOpen toggle) on every trigger click.
    // Since keepOpen prevents outside-click close, this is the only close path that matters.
    const handleMoreClick = useCallback(()=>{
        if (isMoreOpenRef.current) {
            // transitioning open → closed: force-close any sticky children
            registry.closeAll();
        }
        isMoreOpenRef.current = !isMoreOpenRef.current;
        onClickProp?.();
    }, [
        registry,
        onClickProp
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        title: title || i18n.get('more'),
        className: styles.moreItem,
        children: [
            /*#__PURE__*/ _jsx(XMenu.PopupItem, {
                basic: true,
                renderAlways: true,
                position: position,
                className: clsx(styles.more, icon && styles.customIcon, prominent && styles.prominent),
                icon: icon ? icon : 'ellipsis horizontal',
                closeOnChildClick: closeOnChildClick,
                prominent: prominent,
                closeOnPopupDescendantClick: false,
                keepOpen: childOpenCount > 0,
                onClick: handleMoreClick,
                ...props,
                children: /*#__PURE__*/ _jsx(ChildPopupRegistry.Provider, {
                    value: registry,
                    children: /*#__PURE__*/ _jsx(XMenu, {
                        vertical: vertical,
                        attached: false,
                        className: clsx(styles.toolbar, styles.group, styles.more),
                        children: children
                    })
                })
            }),
            activeItems > 0 && /*#__PURE__*/ _jsx("span", {
                className: styles.badge,
                children: activeItems
            })
        ]
    });
};
const ItemList = ({ children })=>/*#__PURE__*/ _jsx("div", {
        className: styles.itemList,
        children: children
    });
// export
// rename out of usability reasons:
Toolbar.Item = XMenu.Item;
Toolbar.Separator = ({ className, props })=>/*#__PURE__*/ _jsx(XMenu.Separator, {
        className: clsx(styles.separator, className),
        ...props
    });
Toolbar.Group = Group;
Toolbar.ItemList = ItemList;
Toolbar.More = More;
// set displayNames
Group.displayName = 'ToolbarGroup';
More.displayName = 'ToolbarMore';
ItemList.displayName = 'ToolbarItemList';
export { Toolbar };
